import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AdapterLuxon, LocalizationProvider, ThemeProvider } from '@/shared/ui/mui';
import {
  ErrorBoundary,
  CookieConsent,
  OfflineIndicator,
  OnlineToast,
  ToastProvider,
} from '@/shared/ui';
import { useLanguage } from '@/app/i18n/useLanguage';
import { AppLayout } from '@/app/layouts';
import { AuthProvider } from '@/app/providers/AuthProvider';
import { AxiosAuthProvider } from '@/app/providers/AxiosAuthProvider';
import { NetworkStatusProvider } from '@/app/providers/NetworkStatusProvider';
import { SocketProvider } from '@/app/providers/realtime';
import { AppRouter } from '@/app/routing';
import {
  usePreloadCommonServiceImages,
} from '@/shared/hooks';
import { registerPWA } from './pwa';
import Theme from './styles/Theme';

// PWA registration is deferred to after initial render (see AppContent)

function AppContent() {
  // Preload common service images for better performance
  usePreloadCommonServiceImages();

  // Keep MUI date pickers in the active locale (Luxon's global locale is kept
  // in sync by the i18n bootstrap's languageChanged listener).
  const { intlLocale } = useLanguage();

  // Defer PWA registration to not block initial render
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      registerPWA();
    }, 2000); // 2 second delay after mount

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <ErrorBoundary enableSentry>
      <ThemeProvider theme={Theme}>
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={intlLocale}>
          <NetworkStatusProvider>
            <ToastProvider>
              <AxiosAuthProvider>
                {/* Global network status indicators */}
                <OfflineIndicator />
                <OnlineToast />

                <BrowserRouter>
                  <AppLayout>
                    <SocketProvider>
                      <AppRouter />
                    </SocketProvider>
                  </AppLayout>
                  <CookieConsent />
                </BrowserRouter>
              </AxiosAuthProvider>
            </ToastProvider>
          </NetworkStatusProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
